import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Paper,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import UserManagementService from "../services/userManagementService";

const PasswordSetResetPage: React.FC = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [isInitialSetup, setIsInitialSetup] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const resetToken = searchParams.get("token");
    const lastName = searchParams.get("lastName");

    if (resetToken) {
      setToken(resetToken);
      setIsInitialSetup(!!lastName);
      if (lastName) {
        setLastName(lastName);
      }
    } else {
      setError(
        "No reset token found in the URL. Please check your reset link."
      );
    }
  }, [location]);

  const validatePassword = (
    password: string
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (password.length < 8)
      errors.push("Password must be at least 8 characters long");
    if (!/[A-Z]/.test(password))
      errors.push("Password must contain at least one uppercase letter");
    if (!/[a-z]/.test(password))
      errors.push("Password must contain at least one lowercase letter");
    if (!/\d/.test(password))
      errors.push("Password must contain at least one number");

    return { isValid: errors.length === 0, errors };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const { isValid, errors } = validatePassword(password);
    if (!isValid) {
      setError(errors.join(". "));
      return;
    }

    if (!token) {
      setError("No reset token available");
      return;
    }

    setLoading(true);
    try {
      const response = isInitialSetup
        ? await UserManagementService.setInitialPassword(
            token,
            password,
            lastName ?? ""
          )
        : await UserManagementService.resetPassword(token, password);

      setSuccess(true);
      // Optionally, redirect to login page after a delay
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(
        `Failed to ${
          isInitialSetup ? "set" : "reset"
        } password. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Alert severity="success">
          Your password has been successfully {isInitialSetup ? "set" : "reset"}
          . You will be redirected to the login page shortly.
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ mt: 4, p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          {isInitialSetup ? "Set Your Password" : "Reset Your Password"}
        </Typography>
        <Box sx={{ mt: 4, maxWidth: 400, mx: "auto" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type="password"
              label={isInitialSetup ? "Initial Password" : "New Password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              type="password"
              label={
                isInitialSetup
                  ? "Confirm Initial Password"
                  : "Confirm New Password"
              }
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Password must:
              </Typography>
              <List dense>
                {[
                  {
                    text: "Be at least 8 characters long",
                    condition: password.length >= 8,
                  },
                  {
                    text: "Contain at least one uppercase letter",
                    condition: /[A-Z]/.test(password),
                  },
                  {
                    text: "Contain at least one lowercase letter",
                    condition: /[a-z]/.test(password),
                  },
                  {
                    text: "Contain at least one number",
                    condition: /\d/.test(password),
                  },
                ].map((requirement, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {requirement.condition ? (
                        <FontAwesomeIcon icon={faCheck} color="success" />
                      ) : (
                        <FontAwesomeIcon icon={faTimes} color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={requirement.text} />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isInitialSetup ? (
                "Set Password"
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        </Box>
      </Paper>
    </Container>
  );
};

export default PasswordSetResetPage;
